import { useEffect, useMemo, useRef } from "react";

import useDataTableState from "./useDataTableState";
import { hasResetValueChanged } from "../utils/hasResetValueChanged";
import { sortingToApi } from "../utils/sortingToApi";

import type { RowData } from "@tanstack/react-table";

import type {
  DataTableServerState,
  ResetPageValue,
  UseDataTableServerStateOptions,
} from "../components/dataTable/dataTable.types";

const NO_RESET_VALUE_LIST: ResetPageValue[] = [];

export default function useDataTableServerState<TData extends RowData>({
  resetPageOn = NO_RESET_VALUE_LIST,
  ...options
}: UseDataTableServerStateOptions<TData>): DataTableServerState {
  const tableState = useDataTableState(options);
  const { state, setPagination } = tableState;
  const { pageIndex, pageSize } = state;
  const previousResetValues = useRef(resetPageOn);
  // Page 1 dès le rendu où la valeur change, sinon ce rendu demanderait l'ancienne page avec la nouvelle valeur.
  const isResetPending =
    pageIndex > 0 &&
    hasResetValueChanged(previousResetValues.current, resetPageOn);

  useEffect(() => {
    if (!hasResetValueChanged(previousResetValues.current, resetPageOn)) return;

    previousResetValues.current = resetPageOn;
    if (pageIndex > 0) setPagination(0, pageSize);
  }, [resetPageOn, pageIndex, pageSize, setPagination]);

  return useMemo(() => {
    const currentState = isResetPending ? { ...state, pageIndex: 0 } : state;

    return {
      page: currentState.pageIndex + 1,
      perPage: currentState.pageSize,
      order: sortingToApi(currentState.sorting),
      search: currentState.search,
      filters: currentState.filters,
      // Le tableau affiche la même page que celle demandée à l'API.
      tableState: isResetPending
        ? { ...tableState, state: currentState }
        : tableState,
    };
  }, [isResetPending, state, tableState]);
}
